import {
  Box,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  CellContext,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import _ from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  date as commonDate,
  typeguards as commonTypeguards,
} from '@finley-technologies/common';

import { colors } from 'packages/common-frontend/colors';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from 'packages/common-frontend/components/icons';
import { ColumnDisplaySettingsButton } from 'packages/common-frontend/components/TanStack/ColumnReordering';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  createDefaultTableColumnConfiguration,
  useTableColumnPreferences,
} from 'packages/common-frontend/hooks/useTableColumnPreferences';
import {
  dollarNumberWithCommas,
  truncateUuid,
} from 'packages/common-frontend/utils';
import { InvoiceStatusBadge } from 'packages/invoices/ui/InvoiceStatusBadge';
import { InvoiceWithComputedStatus } from 'packages/invoices/ui/utils';

const InvoiceTableEmptyHeaderCell = ({
  label,
  justifyContent,
}: {
  label: string;
  justifyContent?: string;
}) => {
  return (
    <Th minH="150px" backgroundColor={colors.background[100]} p="16px">
      <Flex alignItems="center" justifyContent={justifyContent ?? undefined}>
        <Box mr="8px">
          <Icon icon="sort" />
        </Box>
        <Text color="black" fontSize="16px">
          {label}
        </Text>
      </Flex>
    </Th>
  );
};

const InvoicesTableEmptyHeader = () => {
  return (
    <Tr>
      <InvoiceTableEmptyHeaderCell label="Due Date" />
      <InvoiceTableEmptyHeaderCell label="Status" />
      <InvoiceTableEmptyHeaderCell label="Invoice ID" />
      <InvoiceTableEmptyHeaderCell label="Facility" />
      <InvoiceTableEmptyHeaderCell label="Loan ID" />
      <InvoiceTableEmptyHeaderCell label="Amount Due" justifyContent="end" />
      <InvoiceTableEmptyHeaderCell label="Created" />
    </Tr>
  );
};

const InvoicesTableSkeletonRow = () => {
  return (
    <Tr>
      {_.times(7, (idx) => {
        const cellKey = idx;
        return (
          <Td key={cellKey}>
            <Skeleton h="50px" />
          </Td>
        );
      })}
    </Tr>
  );
};

export const InvoicesTableSkeleton = () => {
  return (
    <>
      <Box width="100%" mt="1.5em" mb="0.5em">
        <Typography variant="section-title">Invoices</Typography>
      </Box>
      <Table borderWidth="1px" borderColor={colors.gray[200]}>
        <Thead>
          <InvoicesTableEmptyHeader />
        </Thead>
        <Tbody>
          {_.times(8, (idx) => {
            const rowKey = idx;
            return <InvoicesTableSkeletonRow key={rowKey} />;
          })}
        </Tbody>
      </Table>
    </>
  );
};

export const InvoicesTableEmpty = () => {
  return (
    <>
      <Box width="100%" mt="1.5em" mb="0.5em">
        <Typography variant="section-title">Invoices</Typography>
      </Box>
      <Table borderWidth="1px" borderColor={colors.gray[200]}>
        <Thead>
          <InvoicesTableEmptyHeader />
        </Thead>
        <Tbody>
          <Tr>
            <Td colSpan={7}>There are no invoices to display.</Td>
          </Tr>
        </Tbody>
      </Table>
    </>
  );
};

const InvoiceTableCell = ({
  cellContext,
  formatter,
}: {
  cellContext: CellContext<InvoiceWithComputedStatus, any>;
  formatter: (value: any) => string;
}) => {
  const value = cellContext.getValue();
  return (
    <Text color={colors.black[70]} py="8px">
      {formatter(value)}
    </Text>
  );
};

const COLUMN_DEFS: Array<
  ColumnDef<InvoiceWithComputedStatus, any> & { id: string }
> = [
  {
    id: 'dueDate',
    accessorKey: 'dueDate',
    header: 'Due Date',
    cell: (cellContext) =>
      InvoiceTableCell({
        cellContext,
        formatter: (value) =>
          commonTypeguards.isFullDateStr(value)
            ? commonDate.fullDateStrToHumanDateString(value)
            : '-',
      }),
    minSize: 150,
  },
  {
    id: 'computedStatus',
    accessorKey: 'computedStatus',
    header: 'Status',
    cell: (cellContext) => {
      const StatusCell = () => {
        const computedStatus = cellContext.getValue();

        return <InvoiceStatusBadge computedStatus={computedStatus} />;
      };
      return StatusCell();
    },
    minSize: 150,
  },
  {
    id: 'id',
    accessorKey: 'id',
    header: 'Invoice ID',
    cell: (cellContext) =>
      InvoiceTableCell({
        cellContext,
        formatter: (value) => truncateUuid(value),
      }),
    minSize: 150,
  },
  {
    id: 'facility',
    accessorKey: 'loan.facility',
    header: 'Facility',
    cell: (cellContext) =>
      InvoiceTableCell({
        cellContext,
        formatter: (value) => {
          const externalFacilityIdParentheses = value?.externalFacilityId
            ? ` (${truncateUuid(value.externalFacilityId)})`
            : '';
          return value?.creditFacilityName
            ? `${value.creditFacilityName} ${externalFacilityIdParentheses}`
            : '-';
        },
      }),
    minSize: 150,
  },
  {
    id: 'loanName',
    accessorKey: 'loan.name',
    header: 'Loan Name',
    cell: (cellContext) =>
      InvoiceTableCell({
        cellContext,
        formatter: (value) => value || '-',
      }),
    minSize: 150,
  },
  {
    id: 'finleyLoanId',
    accessorKey: 'loan',
    header: 'Loan ID',
    cell: (cellContext) =>
      InvoiceTableCell({
        cellContext,
        formatter: (value) => {
          const externalLoanIdParentheses = value?.externalLoanId
            ? ` (${truncateUuid(value.externalLoanId)})`
            : '';
          return value?.id
            ? `${truncateUuid(value.id)} ${externalLoanIdParentheses}`
            : '-';
        },
      }),
    minSize: 150,
  },
  {
    id: 'totalAmount',
    accessorKey: 'totalAmount',
    header: 'Amount Due',
    cell: (cellContext) =>
      InvoiceTableCell({
        cellContext,
        formatter: (value) =>
          Number.isFinite(value) ? `${dollarNumberWithCommas(value)}` : '-',
      }),
    minSize: 150,
    meta: {
      style: {
        justifyContent: 'end',
        textAlign: 'right',
      },
    },
  },
  {
    id: 'created_at',
    accessorKey: 'created_at',
    header: 'Created',
    cell: (cellContext) =>
      InvoiceTableCell({
        cellContext,
        formatter: (value) => {
          return commonDate.fullDateStrToHumanDateString(
            commonDate.epochToFullDateStr(value),
          );
        },
      }),
    minSize: 150,
  },
];

const TABLE_KEY = 'invoices.invoicesTable';

const DEFAULT_TABLE_CONFIG = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY,
  columns: COLUMN_DEFS.map((column) => ({
    columnKey: column.id,
    isVisible: column.id !== 'targetInvoiceDate', // never show targetInvoiceDate. only added to columnDef for metadata purposes
    isFixed: false,
  })),
});

const columnNames = COLUMN_DEFS.reduce(
  (acc, column) => ({ ...acc, [column.id]: column.header }),
  {},
);

type InvoicesTableWithDataProps = {
  invoices: InvoiceWithComputedStatus[];
};
const InvoicesTableWithData = ({ invoices }: InvoicesTableWithDataProps) => {
  const navigate = useNavigate();

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true }, // default sorting created_at by desc so most recently created invoice is shown first
  ]);

  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences({
    tableKey: TABLE_KEY,
    defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG,
  });

  // never show targetInvoiceDate. only added to columnDef for metadata purposes
  const visibleColumnConfiguration = columnConfiguration.filter(
    (columnConfig) => columnConfig.columnKey !== 'targetInvoiceDate',
  );

  const table = useReactTable({
    data: invoices,
    columns: COLUMN_DEFS,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  return (
    <>
      <Flex width="100%" justify="space-between" mt="1.5em" mb="0.5em">
        <Typography variant="section-title">Invoices</Typography>
        <Flex alignSelf="baseline" gap="1em">
          <ColumnDisplaySettingsButton
            initialColumns={visibleColumnConfiguration}
            updateColumnConfigs={updateUserColumnConfiguration}
            columnNames={columnNames}
          />
          <CsvXlsxDownloadSelect
            dataRows={csvDownload.dataRows}
            headers={csvDownload.headers}
            filenamePrefix="invoices"
          />
        </Flex>
      </Flex>
      <Flex
        width="100%"
        overflowY="auto"
        borderWidth="1px"
        borderColor={colors.gray[200]}
      >
        <Table cursor="default">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnCanSort = header.column.getCanSort();
                  return (
                    <Th
                      key={header.id}
                      textTransform="capitalize"
                      letterSpacing="normal"
                      padding="8px 16px 8px 16px"
                      position="sticky"
                      top="0"
                      borderBottomWidth="thin"
                      minW={`${header.column.columnDef.minSize}px`}
                      backgroundColor={colors.background[100]}
                    >
                      {header.isPlaceholder ? null : (
                        <Flex
                          alignItems="center"
                          justifyContent={
                            header.column.columnDef.meta?.style?.justifyContent
                          }
                        >
                          <Box
                            cursor={columnCanSort ? 'pointer' : 'default'}
                            onClick={header.column.getToggleSortingHandler()}
                            mr={columnCanSort ? '8px' : '0px'}
                          >
                            {{
                              asc: <Icon icon="sort-up" />,
                              desc: <Icon icon="sort-down" />,
                            }[header.column.getIsSorted() as string] ??
                              (columnCanSort ? <Icon icon="sort" /> : null)}
                          </Box>

                          <Box mr="8px">
                            <Text color="black" fontSize="16px" py="8px">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </Text>
                          </Box>
                        </Flex>
                      )}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody backgroundColor="white">
            {table.getRowModel().rows.map((row) => {
              const invoiceId = row.getValue('id');

              return (
                <Tr
                  key={row.id}
                  _hover={{ backgroundColor: colors.gray[50] }}
                  cursor={invoiceId ? 'pointer' : 'default'}
                  onClick={() => {
                    if (invoiceId) {
                      navigate(`/invoices/${invoiceId}`);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      textAlign={cell.column.columnDef.meta?.style?.textAlign}
                      padding={
                        cell.column.columnDef.meta?.style?.padding || '8px 16px'
                      }
                      height="56px"
                      minW={`${cell.column.columnDef.minSize}px`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Flex>
    </>
  );
};

type InvoicesTableProps = {
  invoices: InvoiceWithComputedStatus[] | undefined;
  isLoading: boolean;
};
export const InvoicesTable = ({ invoices, isLoading }: InvoicesTableProps) => {
  if (isLoading || !invoices) {
    return <InvoicesTableSkeleton />;
  }

  if (!invoices.length) {
    return <InvoicesTableEmpty />;
  }

  return <InvoicesTableWithData invoices={invoices} />;
};
